import React, { useState, useEffect, useCallback } from 'react';

interface MarketBotAdminPanelProps {
  apiBaseUrl: string;
  adminKey: string;
}

interface MarketBot {
  id: string;
  name: string;
  personality: 'aggressive' | 'balanced' | 'conservative' | 'specialist';
  region: string;
  berries: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BotConfig {
  enabled: boolean;
  tickIntervalSeconds: number;
  maxOrdersPerTick: number;
  priceVariancePercent: number;
  minSpreadPercent: number;
  orderFillProbability: number;
  newOrderProbability: number;
  maxActiveOrdersPerBot: number;
  orderQuantityMin: number;
  orderQuantityMax: number;
  targetItems: string[];
  enableItemGeneration?: boolean;
  itemGenerationRate?: number;
  maxItemsGeneratedPerTick?: number;
  fulfillUnfilledOrdersProbability?: number;
}

interface BotTradingSummary {
  totalBots: number;
  activeBots: number;
  totalOrdersToday: number;
  totalVolumeToday: number;
  totalBerriesInCirculation: number;
  botsEnabled: boolean;
}

interface BotActivityLog {
  id: string;
  botId: string;
  botName: string;
  actionType: string;
  itemId: string;
  quantity: number;
  pricePerUnit: number;
  totalValue: number;
  region: string;
  createdAt: string;
}

interface BotStats {
  bot: MarketBot;
  inventory: Array<{ botId: string; itemId: string; quantity: number }>;
  activeOrders: number;
  ordersPlacedToday: number;
  volumeToday: number;
}

const StatCard: React.FC<{ label: string; value: string | number; color?: string }> = ({ label, value, color }) => (
  <div style={{ padding: '12px 16px', backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', minWidth: 100 }}>
    <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 9, color: '#718096', textTransform: 'uppercase', marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 16, fontWeight: 600, color: color || '#e2e8f0' }}>
      {typeof value === 'number' ? value.toLocaleString() : value}
    </div>
  </div>
);

export const MarketBotAdminPanel: React.FC<MarketBotAdminPanelProps> = ({ apiBaseUrl, adminKey }) => {
  const [summary, setSummary] = useState<BotTradingSummary | null>(null);
  const [bots, setBots] = useState<MarketBot[]>([]);
  const [config, setConfig] = useState<BotConfig | null>(null);
  const [activity, setActivity] = useState<BotActivityLog[]>([]);
  const [selectedBot, setSelectedBot] = useState<BotStats | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [showCreateBot, setShowCreateBot] = useState<boolean>(false);
  const [newBot, setNewBot] = useState<{ name: string; personality: string; region: string; berries: number }>({
    name: '',
    personality: 'balanced',
    region: 'reaches',
    berries: 1000000,
  });

  const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
    return fetch(`${apiBaseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-admin-key': adminKey,
        ...options.headers,
      },
    });
  }, [apiBaseUrl, adminKey]);

  const showMessage = (text: string, type: 'success' | 'error'): void => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchSummary = useCallback(async (): Promise<void> => {
    try {
      const res = await fetchWithAuth('/api/admin/bots/summary');
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch bot summary:', err);
    }
  }, [fetchWithAuth]);

  const fetchBots = useCallback(async (): Promise<void> => {
    try {
      const res = await fetchWithAuth('/api/admin/bots/bots');
      if (res.ok) {
        const data = await res.json();
        setBots(data.bots || []);
      }
    } catch (err) {
      console.error('Failed to fetch bots:', err);
    }
  }, [fetchWithAuth]);

  const fetchConfig = useCallback(async (): Promise<void> => {
    try {
      const res = await fetchWithAuth('/api/admin/bots/config');
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [fetchWithAuth]);

  const fetchActivity = useCallback(async (): Promise<void> => {
    try {
      const res = await fetchWithAuth('/api/admin/bots/activity?limit=20');
      if (res.ok) {
        const data = await res.json();
        setActivity(data.activity || []);
      }
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
  }, [fetchWithAuth]);

  const fetchBotStats = async (botId: string): Promise<void> => {
    try {
      const res = await fetchWithAuth(`/api/admin/bots/bots/${botId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedBot(data);
      }
    } catch (err) {
      console.error('Failed to fetch bot stats:', err);
    }
  };

  const toggleBots = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/admin/bots/toggle', {
        method: 'POST',
        body: JSON.stringify({ enabled: !summary?.botsEnabled }),
      });
      if (res.ok) {
        showMessage(summary?.botsEnabled ? 'Bots disabled' : 'Bots enabled', 'success');
        await fetchSummary();
      } else {
        showMessage('Failed to toggle bots', 'error');
      }
    } catch (err) {
      showMessage('Failed to toggle bots', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const forceTick = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/admin/bots/force-tick', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        showMessage(`Tick completed in ${data.processingTime}ms`, 'success');
        await Promise.all([fetchSummary(), fetchActivity()]);
      } else {
        showMessage('Failed to force tick', 'error');
      }
    } catch (err) {
      showMessage('Failed to force tick', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const forceActivity = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/admin/bots/force-activity', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        showMessage(`Activity forced: ${data.ordersPlaced} orders, ${data.itemsGenerated} items generated`, 'success');
        await Promise.all([fetchSummary(), fetchActivity()]);
      } else {
        showMessage('Failed to force activity', 'error');
      }
    } catch (err) {
      showMessage('Failed to force activity', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const createBot = async (): Promise<void> => {
    if (!newBot.name.trim()) {
      showMessage('Bot name is required', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/api/admin/bots/bots', {
        method: 'POST',
        body: JSON.stringify(newBot),
      });
      if (res.ok) {
        showMessage(`Bot "${newBot.name}" created`, 'success');
        setShowCreateBot(false);
        setNewBot({ name: '', personality: 'balanced', region: 'reaches', berries: 1000000 });
        await Promise.all([fetchBots(), fetchSummary()]);
      } else {
        showMessage('Failed to create bot', 'error');
      }
    } catch (err) {
      showMessage('Failed to create bot', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleBotActive = async (bot: MarketBot): Promise<void> => {
    try {
      const res = await fetchWithAuth(`/api/admin/bots/bots/${bot.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !bot.isActive }),
      });
      if (res.ok) {
        showMessage(`Bot ${bot.isActive ? 'deactivated' : 'activated'}`, 'success');
        await fetchBots();
      }
    } catch (err) {
      showMessage('Failed to update bot', 'error');
    }
  };

  const deleteBot = async (bot: MarketBot): Promise<void> => {
    if (!window.confirm(`Delete bot "${bot.name}"? This will cancel all their orders.`)) return;
    try {
      const res = await fetchWithAuth(`/api/admin/bots/bots/${bot.id}`, { method: 'DELETE' });
      if (res.ok) {
        showMessage(`Bot "${bot.name}" deleted`, 'success');
        setSelectedBot(null);
        await Promise.all([fetchBots(), fetchSummary()]);
      }
    } catch (err) {
      showMessage('Failed to delete bot', 'error');
    }
  };

  const replenishBerries = async (botId: string, amount: number): Promise<void> => {
    try {
      const res = await fetchWithAuth(`/api/admin/bots/bots/${botId}/berries`, {
        method: 'POST',
        body: JSON.stringify({ amount }),
      });
      if (res.ok) {
        showMessage(`Added ${amount.toLocaleString()} berries`, 'success');
        await fetchBotStats(botId);
        await fetchBots();
      }
    } catch (err) {
      showMessage('Failed to replenish berries', 'error');
    }
  };

  const replenishInventory = async (botId: string, itemId: string, quantity: number): Promise<void> => {
    try {
      const res = await fetchWithAuth(`/api/admin/bots/bots/${botId}/inventory`, {
        method: 'POST',
        body: JSON.stringify({ itemId, quantity }),
      });
      if (res.ok) {
        showMessage(`Added ${quantity}x ${itemId}`, 'success');
        await fetchBotStats(botId);
      }
    } catch (err) {
      showMessage('Failed to replenish inventory', 'error');
    }
  };

  const updateConfig = async (key: string, value: unknown): Promise<void> => {
    const previousConfig = config;
    try {
      setConfig(prev => prev ? { ...prev, [key]: value } : null);
      const res = await fetchWithAuth('/api/admin/bots/config', {
        method: 'PATCH',
        body: JSON.stringify({ [key]: value }),
      });
      if (res.ok) {
        showMessage('Config updated', 'success');
      } else {
        showMessage('Failed to update config', 'error');
        setConfig(previousConfig);
      }
    } catch (err) {
      showMessage('Failed to update config', 'error');
      setConfig(previousConfig);
    }
  };

  useEffect(() => {
    void fetchSummary();
    void fetchBots();
    void fetchConfig();
    void fetchActivity();

    const interval = setInterval(() => {
      void fetchSummary();
      void fetchActivity();
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const personalityColors: Record<string, string> = {
    aggressive: '#ef4444',
    balanced: '#3b82f6',
    conservative: '#22c55e',
    specialist: '#a855f7',
  };

  const actionTypeColors: Record<string, string> = {
    place_buy: '#22c55e',
    place_sell: '#ef4444',
    fill_order: '#3b82f6',
    order_filled: '#f59e0b',
    order_expired: '#718096',
    cancel_order: '#a855f7',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Message Toast */}
      {message && (
        <div style={{
          position: 'fixed',
          top: 20,
          right: 20,
          padding: '12px 20px',
          backgroundColor: message.type === 'success' ? '#22c55e' : '#ef4444',
          color: '#fff',
          borderRadius: 4,
          zIndex: 1000,
          fontFamily: '"Sometype Mono", monospace',
          fontSize: 12,
        }}>
          {message.text}
        </div>
      )}

      {/* Header with Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0, color: '#ffa500', fontFamily: '"Sometype Mono", monospace', fontSize: 14 }}>
            🤖 MARKET BOTS
          </h3>
          <span style={{
            padding: '4px 8px',
            backgroundColor: summary?.botsEnabled ? '#22c55e22' : '#ef444422',
            color: summary?.botsEnabled ? '#22c55e' : '#ef4444',
            fontSize: 10,
            borderRadius: 4,
            fontWeight: 600,
          }}>
            {summary?.botsEnabled ? 'ACTIVE' : 'DISABLED'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={toggleBots}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              backgroundColor: summary?.botsEnabled ? '#ef4444' : '#22c55e',
              color: '#fff',
              border: 'none',
              fontSize: 11,
              cursor: isLoading ? 'default' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {summary?.botsEnabled ? 'Disable Bots' : 'Enable Bots'}
          </button>
          <button
            type="button"
            onClick={forceTick}
            disabled={isLoading || !summary?.botsEnabled}
            style={{
              padding: '6px 12px',
              backgroundColor: '#3b82f6',
              color: '#fff',
              border: 'none',
              fontSize: 11,
              cursor: isLoading || !summary?.botsEnabled ? 'default' : 'pointer',
              opacity: isLoading || !summary?.botsEnabled ? 0.5 : 1,
            }}
          >
            Force Tick
          </button>
          <button
            type="button"
            onClick={() => setShowCreateBot(true)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#a855f7',
              color: '#fff',
              border: 'none',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            + New Bot
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatCard label="Total Bots" value={summary?.totalBots || 0} />
        <StatCard label="Active Bots" value={summary?.activeBots || 0} color="#22c55e" />
        <StatCard label="Orders Today" value={summary?.totalOrdersToday || 0} color="#3b82f6" />
        <StatCard label="Volume Today" value={summary?.totalVolumeToday || 0} color="#f59e0b" />
        <StatCard label="Bot Berries" value={summary?.totalBerriesInCirculation || 0} color="#a855f7" />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Bot List */}
        <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
          <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
            Bot Characters ({bots.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
            {bots.map((bot) => (
              <div
                key={bot.id}
                onClick={() => fetchBotStats(bot.id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: selectedBot?.bot.id === bot.id ? '#1a1a2e' : '#0a0a12',
                  border: selectedBot?.bot.id === bot.id ? '1px solid #3b82f6' : '1px solid transparent',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: bot.isActive ? '#22c55e' : '#ef4444',
                  }} />
                  <span style={{ color: '#e2e8f0', fontSize: 12 }}>{bot.name}</span>
                  <span style={{
                    padding: '2px 6px',
                    backgroundColor: personalityColors[bot.personality] + '22',
                    color: personalityColors[bot.personality],
                    fontSize: 9,
                    borderRadius: 3,
                  }}>
                    {bot.personality}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#f59e0b', fontSize: 10 }}>₱{bot.berries.toLocaleString()}</span>
                  <span style={{ color: '#718096', fontSize: 10 }}>{bot.region}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bot Details / Activity */}
        <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
          {selectedBot ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', textTransform: 'uppercase' }}>
                  {selectedBot.bot.name}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => toggleBotActive(selectedBot.bot)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: selectedBot.bot.isActive ? '#ef4444' : '#22c55e',
                      color: '#fff',
                      border: 'none',
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >
                    {selectedBot.bot.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteBot(selectedBot.bot)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#1f2937',
                      color: '#ef4444',
                      border: '1px solid #ef4444',
                      fontSize: 10,
                      cursor: 'pointer',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Bot Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Berries</div>
                  <div style={{ fontSize: 14, color: '#f59e0b', fontWeight: 600 }}>₱{selectedBot.bot.berries.toLocaleString()}</div>
                </div>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Active Orders</div>
                  <div style={{ fontSize: 14, color: '#3b82f6', fontWeight: 600 }}>{selectedBot.activeOrders}</div>
                </div>
                <div style={{ padding: 8, backgroundColor: '#1a1a2e', borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: '#718096' }}>Orders Today</div>
                  <div style={{ fontSize: 14, color: '#22c55e', fontWeight: 600 }}>{selectedBot.ordersPlacedToday}</div>
                </div>
              </div>

              {/* Inventory */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, color: '#718096', marginBottom: 8 }}>Inventory</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedBot.inventory.length > 0 ? (
                    selectedBot.inventory.map((item) => (
                      <span key={item.itemId} style={{ padding: '4px 8px', backgroundColor: '#1a1a2e', borderRadius: 4, fontSize: 10, color: '#e2e8f0' }}>
                        {item.itemId}: {item.quantity}
                      </span>
                    ))
                  ) : (
                    <span style={{ color: '#718096', fontSize: 10 }}>Empty</span>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => replenishBerries(selectedBot.bot.id, 1000000)}
                  style={{ padding: '6px 10px', backgroundColor: '#2d3748', color: '#f59e0b', border: '1px solid #4a5568', fontSize: 10, cursor: 'pointer' }}
                >
                  +1M Berries
                </button>
                <button
                  type="button"
                  onClick={() => replenishInventory(selectedBot.bot.id, 'wood', 100)}
                  style={{ padding: '6px 10px', backgroundColor: '#2d3748', color: '#22c55e', border: '1px solid #4a5568', fontSize: 10, cursor: 'pointer' }}
                >
                  +100 Wood
                </button>
                <button
                  type="button"
                  onClick={() => replenishInventory(selectedBot.bot.id, 'iron_ore', 100)}
                  style={{ padding: '6px 10px', backgroundColor: '#2d3748', color: '#22c55e', border: '1px solid #4a5568', fontSize: 10, cursor: 'pointer' }}
                >
                  +100 Iron
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
                Recent Activity
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                {activity.length > 0 ? (
                  activity.map((log) => (
                    <div key={log.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', backgroundColor: '#1a1a2e', borderRadius: 4, borderLeft: `3px solid ${actionTypeColors[log.actionType] || '#718096'}` }}>
                      <span style={{ fontSize: 9, color: '#718096', minWidth: 50 }}>
                        {new Date(log.createdAt).toLocaleTimeString()}
                      </span>
                      <span style={{ fontSize: 10, color: '#a0aec0' }}>{log.botName}</span>
                      <span style={{ fontSize: 10, color: actionTypeColors[log.actionType] || '#e2e8f0' }}>
                        {log.actionType.replace('_', ' ')}
                      </span>
                      <span style={{ fontSize: 10, color: '#e2e8f0' }}>
                        {log.quantity}x {log.itemId} @ ₱{log.pricePerUnit}
                      </span>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#718096', fontSize: 11, textAlign: 'center', padding: 20 }}>No recent activity</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Configuration Panel */}
      {config && (
        <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 16 }}>
          <div style={{ fontFamily: '"Sometype Mono", monospace', fontSize: 11, color: '#ffa500', marginBottom: 12, textTransform: 'uppercase', borderBottom: '1px solid #2a2a3e', paddingBottom: 8 }}>
            Configuration
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <div>
              <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Tick Interval (s)</label>
              <input
                type="number"
                value={config.tickIntervalSeconds}
                onChange={(e) => updateConfig('tickIntervalSeconds', parseInt(e.target.value, 10))}
                style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Max Orders/Tick</label>
              <input
                type="number"
                value={config.maxOrdersPerTick}
                onChange={(e) => updateConfig('maxOrdersPerTick', parseInt(e.target.value, 10))}
                style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Price Variance %</label>
              <input
                type="number"
                value={config.priceVariancePercent}
                onChange={(e) => updateConfig('priceVariancePercent', parseInt(e.target.value, 10))}
                style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Order Probability</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={config.newOrderProbability}
                onChange={(e) => updateConfig('newOrderProbability', parseFloat(e.target.value))}
                style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create Bot Modal */}
      {showCreateBot && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 24, minWidth: 400 }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#ffa500', fontFamily: '"Sometype Mono", monospace' }}>Create New Bot</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Name</label>
                <input
                  type="text"
                  value={newBot.name}
                  onChange={(e) => setNewBot({ ...newBot, name: e.target.value })}
                  placeholder="Bot name..."
                  style={{ width: '100%', padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Personality</label>
                <select
                  value={newBot.personality}
                  onChange={(e) => setNewBot({ ...newBot, personality: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
                >
                  <option value="balanced">Balanced</option>
                  <option value="aggressive">Aggressive</option>
                  <option value="conservative">Conservative</option>
                  <option value="specialist">Specialist</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Region</label>
                <select
                  value={newBot.region}
                  onChange={(e) => setNewBot({ ...newBot, region: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
                >
                  <option value="reaches">The Reaches</option>
                  <option value="paradise">Paradise</option>
                  <option value="arcadia">Arcadia</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 10, color: '#718096', display: 'block', marginBottom: 4 }}>Starting Berries</label>
                <input
                  type="number"
                  value={newBot.berries}
                  onChange={(e) => setNewBot({ ...newBot, berries: parseInt(e.target.value, 10) })}
                  style={{ width: '100%', padding: '8px 12px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 12 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={createBot}
                  disabled={isLoading}
                  style={{ flex: 1, padding: '10px', backgroundColor: '#22c55e', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer' }}
                >
                  Create Bot
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateBot(false)}
                  style={{ flex: 1, padding: '10px', backgroundColor: '#2d3748', color: '#e2e8f0', border: '1px solid #4a5568', fontSize: 12, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Config Controls */}
      {config && (
        <div style={{
          backgroundColor: '#1a1a2e',
          border: '1px solid #2a2a3e',
          padding: 16,
          marginTop: 16,
        }}>
          <h3 style={{ color: '#ffa500', fontSize: 14, margin: '0 0 16px 0' }}>Real-time Config Controls</h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 16,
          }}>
            {/* Item Generation Controls */}
            <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12, borderRadius: 4 }}>
              <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Item Generation</div>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.enableItemGeneration ?? false}
                  onChange={(e) => updateConfig('enableItemGeneration', e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ color: '#a0aec0', fontSize: 11 }}>Enable Item Generation</span>
              </label>

              <div style={{ marginBottom: 8 }}>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  Generation Rate: {((config.itemGenerationRate ?? 0.7) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(config.itemGenerationRate ?? 0.7) * 100}
                  onChange={(e) => updateConfig('itemGenerationRate', parseInt(e.target.value, 10) / 100)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  Max Items/Tick: {config.maxItemsGeneratedPerTick ?? 20}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={config.maxItemsGeneratedPerTick ?? 20}
                  onChange={(e) => updateConfig('maxItemsGeneratedPerTick', parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 11 }}
                />
              </div>
            </div>

            {/* Order Fulfillment Controls */}
            <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12, borderRadius: 4 }}>
              <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Order Fulfillment</div>
              
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  Fulfillment Probability: {((config.fulfillUnfilledOrdersProbability ?? 0.8) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(config.fulfillUnfilledOrdersProbability ?? 0.8) * 100}
                  onChange={(e) => updateConfig('fulfillUnfilledOrdersProbability', parseInt(e.target.value, 10) / 100)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  Tick Interval: {config.tickIntervalSeconds}s
                </label>
                <input
                  type="number"
                  min="10"
                  max="300"
                  value={config.tickIntervalSeconds}
                  onChange={(e) => updateConfig('tickIntervalSeconds', parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 11 }}
                />
              </div>
            </div>

            {/* Trading Controls */}
            <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12, borderRadius: 4 }}>
              <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>💱 Trading Behavior</div>
              
              <div style={{ marginBottom: 8 }}>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  New Order Probability: {((config.newOrderProbability ?? 0.5) * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={(config.newOrderProbability ?? 0.5) * 100}
                  onChange={(e) => updateConfig('newOrderProbability', parseInt(e.target.value, 10) / 100)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                  Max Orders/Tick: {config.maxOrdersPerTick ?? 10}
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={config.maxOrdersPerTick ?? 10}
                  onChange={(e) => updateConfig('maxOrdersPerTick', parseInt(e.target.value, 10))}
                  style={{ width: '100%', padding: '6px 8px', backgroundColor: '#1a1a2e', border: '1px solid #2a2a3e', color: '#e2e8f0', fontSize: 11 }}
                />
              </div>
            </div>

            {/* System Controls */}
            <div style={{ backgroundColor: '#0d0d14', border: '1px solid #2a2a3e', padding: 12, borderRadius: 4 }}>
              <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>System</div>
              
              <button
                onClick={toggleBots}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: 8,
                  backgroundColor: summary?.botsEnabled ? '#ef4444' : '#22c55e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                {summary?.botsEnabled ? 'Pause Bots' : 'Resume Bots'}
              </button>

              <button
                onClick={forceTick}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: 8,
                  backgroundColor: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                Force Tick
              </button>

              <button
                onClick={forceActivity}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: isLoading ? 'default' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                }}
              >
                Force Activity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketBotAdminPanel;
